// src/utils/gpsTrackerDBOperations/index.js
//
// Every query against the GPS tracker Supabase project — a separate instance
// from the app's own database (VITE_GPS_TRACKER_URL). Read-only, online-only,
// and used only by the patrol report workflow (src/utils/api/patrolReport), so
// each query is written out individually. Every function returns the client's
// own result.

import { supabase_gps_tracker } from "src/utils/supabaseClient";
import Constants from "src/utils/constants";

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

/** Fleet Complete GPS points for one date, newest first on each orderBy field. */
async function getFleetCompleteGpsByDate(date, orderBy) {
  let query = supabase_gps_tracker
    .from(Constants.enums.TABLES_NAME_ENUM.FLEET_COMPLETE_GPS_TABLE)
    .select("*")
    .eq("Date", date);

  if (Array.isArray(orderBy)) {
    orderBy.forEach((field) => {
      if (typeof field === "string") {
        query = query.order(field, { ascending: false });
      }
    });
  }

  return await query;
}

/** One page of CMA 519 users' location points between two ISO timestamps. */
async function getCma519UserLocations(startTime, endTime, from, to, orderBy) {
  let query = supabase_gps_tracker
    .from(Constants.enums.TABLES_NAME_ENUM.CMA_519_USERS_LAT_LONG_TABLE)
    .select("latitude, longitude, timestamp, userDisplayName, deviceId")
    .gt("timestamp", startTime)
    .lt("timestamp", endTime)
    .range(from, to);

  if (Array.isArray(orderBy)) {
    orderBy.forEach((field) => {
      if (typeof field === "string") {
        query = query.order(field, { ascending: false });
      }
    });
  }

  return await query;
}

/** QC-check patrol status records for a week, by highway then date. */
async function getPatrolStatusForWeek(weekStartIso, weekEndIso) {
  return await supabase_gps_tracker
    .from(Constants.enums.TABLES_NAME_ENUM.PATROL_STATUS_TABLE)
    .select("*")
    .gte("patrolDate", weekStartIso)
    .lte("patrolDate", weekEndIso)
    .eq("status", Constants.enums.QC_STATUS_OPTIONS.QC_CHECK)
    .eq("is_deleted", false)
    .order("hwyUuid", { ascending: true })
    .order("patrolDate", { ascending: true })
    .order("coverage_percentage", { ascending: false });
}

/** Patrol status records (any status) for one highway on one day. */
async function getPatrolStatusForHighwayAndDay(hwyUuid, startIso, endIso) {
  return await supabase_gps_tracker
    .from(Constants.enums.TABLES_NAME_ENUM.PATROL_STATUS_TABLE)
    .select("*")
    .eq("hwyUuid", hwyUuid)
    .gte("patrolDate", startIso)
    .lte("patrolDate", endIso)
    .eq("is_deleted", false)
    .order("patrolDate", { ascending: true })
    .order("coverage_percentage", { ascending: false });
}

// ---------------------------------------------------------------------------
// Postgres functions on the GPS tracker project
// ---------------------------------------------------------------------------

async function getPatrolStatusReport(weekDate, areaIds, regionId) {
  return await supabase_gps_tracker.rpc(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_PATROL_STATUS_REPORT,
    { week_date: weekDate, p_area_ids: areaIds, p_region_id: regionId }
  );
}

async function getPatrolReportGpsData({
  highwayId,
  date,
  deviceId,
  truckId,
  fromHour,
  toHour
}) {
  return await supabase_gps_tracker.rpc(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_WEEKLY_PATROL_REPORT_GPS_DATA,
    {
      p_highway_id: highwayId,
      p_patrol_date: date,
      p_device_id: deviceId,
      p_truck_id: truckId,
      p_from_hour: fromHour,
      p_to_hour: toHour
    }
  );
}

async function getPatrolEvents(deviceId, date) {
  return await supabase_gps_tracker.rpc(
    Constants.enums.DATABASE_FUNCTION_ENUM.GET_PATROL_EVENTS,
    {
      p_patrol_date: date,
      p_device_id: deviceId
    }
  );
}

export default {
  getFleetCompleteGpsByDate,
  getCma519UserLocations,
  getPatrolStatusForWeek,
  getPatrolStatusForHighwayAndDay,
  getPatrolStatusReport,
  getPatrolReportGpsData,
  getPatrolEvents
};
